/**
 * Outlook / Microsoft Calendar — frontend service.
 * All calls route through a single 'outlookCalendar' Firebase function via { action }.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';

const CLIENT_ID = '8034985f-141d-4c94-8863-49e900d8867f';
const SCOPES = 'Calendars.ReadWrite Calendars.ReadWrite.Shared User.Read offline_access';
export const OUTLOOK_REDIRECT_URI = `${window.location.origin}/outlook-calendar-callback`;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OutlookCalendarEvent {
  id: string; rawEventId: string; calendarId: string; calendarName?: string;
  calendarColor?: string; accountEmail: string; title: string; start: string; end: string;
  isAllDay: boolean; location?: string; description?: string; htmlLink?: string;
  attendees?: Array<{ email: string; displayName?: string; responseStatus?: string }>;
  organizerEmail?: string; showAs?: string;
}

export interface OutlookCalendarListEntry {
  id: string; name: string; color?: string; isDefault?: boolean;
}

export interface OutlookAccount {
  email: string; displayName?: string; connectedAt?: string | null; tokenExpired?: boolean;
}

// ─── PKCE helpers ─────────────────────────────────────────────────────────────

function base64UrlEncode(buffer: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

async function generatePkce(): Promise<{ codeVerifier: string; codeChallenge: string }> {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  const codeVerifier = base64UrlEncode(array.buffer);
  const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier));
  return { codeVerifier, codeChallenge: base64UrlEncode(hash) };
}

// ─── Service ──────────────────────────────────────────────────────────────────

class OutlookCalendarService {
  private fn = httpsCallable<{ action: string; [key: string]: unknown }, Record<string, unknown>>(
    getFunctions(), 'outlookCalendar'
  );

  async connect(): Promise<string> {
    const { codeVerifier, codeChallenge } = await generatePkce();
    const params = new URLSearchParams({
      client_id: CLIENT_ID, response_type: 'code', redirect_uri: OUTLOOK_REDIRECT_URI,
      scope: SCOPES, response_mode: 'query', code_challenge: codeChallenge, code_challenge_method: 'S256',
      state: 'popup',
    });
    const authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?${params}`;

    return new Promise((resolve, reject) => {
      const channel = new BroadcastChannel('outlook-auth');

      // Store verifier in localStorage so the popup's early-intercept script can read it.
      // (The popup is a fresh window and cannot access the main window's sessionStorage.)
      localStorage.setItem('_outlookPkceVerifier', codeVerifier);

      const popup = window.open(authUrl, 'outlook-auth', 'width=520,height=700,resizable,scrollbars');
      if (!popup) {
        channel.close();
        localStorage.removeItem('_outlookPkceVerifier');
        reject(new Error('Popup was blocked. Please allow popups for this site.'));
        return;
      }

      // Signal to the SW reload guard that auth is in flight (prevents autoUpdate reload mid-OAuth).
      sessionStorage.setItem('outlookAuthInProgress', '1');

      const cleanup = () => {
        sessionStorage.removeItem('outlookAuthInProgress');
        localStorage.removeItem('_outlookPkceVerifier');
        window.removeEventListener('storage', storageHandler);
        channel.close();
        clearTimeout(timeoutId);
      };

      // The popup exchanges the code for tokens (using its own fresh CSP) and writes
      // { accessToken, refreshToken, expiresIn, scope } or { error } to localStorage.
      const storageHandler = async (e: StorageEvent) => {
        if (e.key !== '_outlookAuth') return;
        cleanup();
        localStorage.removeItem('_outlookAuth');
        const payload = JSON.parse(e.newValue ?? '{}') as {
          accessToken?: string; refreshToken?: string; expiresIn?: number; scope?: string; error?: string;
        };
        if (payload.error || !payload.accessToken || !payload.refreshToken) {
          reject(new Error(payload.error === 'access_denied' ? 'Access was denied.' : (payload.error ?? 'Authorization failed.')));
          return;
        }
        try {
          const result = await this.fn({
            action: 'saveTokens',
            accessToken: payload.accessToken,
            refreshToken: payload.refreshToken,
            expiresIn: payload.expiresIn ?? 3600,
            scope: payload.scope ?? '',
          });
          resolve((result.data as { email: string }).email);
        } catch (err) { reject(err); }
      };
      window.addEventListener('storage', storageHandler);

      // Reject after 10 min if popup is abandoned (can't poll popup.closed due to COOP).
      const timeoutId = setTimeout(() => {
        cleanup();
        reject(new Error('Authentication timed out. Please try again.'));
      }, 10 * 60 * 1000);
    });
  }

  async getStatus(): Promise<{ connected: boolean; accounts: OutlookAccount[] }> {
    return (await this.fn({ action: 'getStatus' })).data as { connected: boolean; accounts: OutlookAccount[] };
  }

  async getCalendarList(accountEmail: string): Promise<OutlookCalendarListEntry[]> {
    const result = await this.fn({ action: 'getCalendarList', accountEmail });
    return (result.data as { calendars: OutlookCalendarListEntry[] }).calendars;
  }

  async getEvents(start: Date, end: Date): Promise<OutlookCalendarEvent[]> {
    const result = await this.fn({ action: 'getEvents', startIso: start.toISOString(), endIso: end.toISOString() });
    return (result.data as { events: OutlookCalendarEvent[] }).events;
  }

  async disconnect(accountEmail: string): Promise<void> {
    await this.fn({ action: 'disconnect', accountEmail });
  }

  async createEvent(params: { accountEmail: string; calendarId: string; title: string; start: string; end: string; isAllDay?: boolean; timeZone?: string; location?: string; description?: string; attendees?: Array<{ email: string; name?: string }> }): Promise<{ eventId: string }> {
    return (await this.fn({ action: 'createEvent', ...params })).data as { eventId: string };
  }

  async updateEvent(params: { accountEmail: string; calendarId: string; eventId: string; title?: string; start?: string; end?: string; isAllDay?: boolean; timeZone?: string; location?: string; description?: string; attendees?: Array<{ email: string; name?: string }> }): Promise<void> {
    await this.fn({ action: 'updateEvent', ...params });
  }

  async deleteEvent(accountEmail: string, calendarId: string, eventId: string): Promise<void> {
    await this.fn({ action: 'deleteEvent', accountEmail, calendarId, eventId });
  }
}

export const outlookCalendarService = new OutlookCalendarService();
